import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import { orgProjectKeys } from "@/lib/org-project-context/coordinator-query";
import { useScopeKey } from "@/lib/org-project-context/hooks";
import { queryClient } from "@/lib/query-client";
import { useAcceptInvitation } from "../api/hooks";
import type { GroupedInvitation } from "../types";

type AcceptAndSwitchOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export function useAcceptAndSwitch(options?: AcceptAndSwitchOptions) {
  const navigate = useNavigate({ from: ORG_PROJECT_ROUTE_PATH });
  const scopeKey = useScopeKey();
  const { mutate: acceptInvitation, isPending } = useAcceptInvitation();

  const acceptAndSwitch = (invitation: GroupedInvitation) => {
    const { primary_invitation_id, project_id, org_id, project_name } =
      invitation;

    if (!primary_invitation_id) {
      toast.error("Invalid invitation");
      return;
    }

    acceptInvitation(
      { invitationId: primary_invitation_id },
      {
        onSuccess: async () => {
          try {
            await queryClient.invalidateQueries({
              queryKey: orgProjectKeys.all(scopeKey),
            });

            toast.success(`Switched to ${project_name}`);
            await navigate({
              params: () => ({
                organizationId: org_id,
                projectId: project_id,
              }),
              to: "./home",
            });
            options?.onSuccess?.();
          } catch (error) {
            const message =
              error instanceof Error ? error.message : "Unknown error";
            console.error("Error switching project:", error);
            toast.error(`Failed to switch: ${message}`);
            options?.onError?.(
              error instanceof Error ? error : new Error(message)
            );
          }
        },
      }
    );
  };

  return {
    acceptAndSwitch,
    isPending,
  };
}
